import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp, Users, PieChart as PieIcon, Layers } from "lucide-react";

const MINE = "#0B8F78";
const CLIENTS = "#4060FF";
const CAPITAL = "#C2185B";
const PLAN_COLOURS = ["#4060FF", "#0B8F78", "#C58B3C", "#C2185B", "#7C3AED", "#0EA5E9"];

const usd0 = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const usd2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const compact = (n) => {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}m`;
  if (v >= 1_000) return `$${Math.round(n / 1000)}k`;
  return `$${Math.round(n)}`;
};
const monLabel = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-");
  return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][+mm - 1]} ${String(y).slice(2)}`;
};
const FIRM_WORDS = ["ltd", "llp", "llc", "inc", "plc", "trust", "gmbh", "sa", "bv", "co"];
const shortName = (n) => {
  let bits = String(n || "").trim().split(/\s+/).filter(Boolean);
  if (["the", "mr", "mrs", "ms", "dr"].includes((bits[0] || "").toLowerCase())) bits = bits.slice(1);
  const s = bits.join(" ");
  if (s.length <= 15) return s;
  // A firm keeps its name ("Caldwell Brothers"); a person gets an initial.
  if (FIRM_WORDS.includes(bits[bits.length - 1].replace(/\./g, "").toLowerCase())) {
    return bits.slice(0, 2).join(" ");
  }
  return bits.length > 1 ? `${bits[0]} ${bits[bits.length - 1][0]}.` : `${s.slice(0, 14)}…`;
};

const axis = { fontSize: 10.5, fill: "rgba(15,18,38,0.5)" };
const grid = "rgba(15,18,38,0.06)";

const Box = ({ title, sub, right, children, testid }) => (
  <div className="glass p-4" data-testid={testid}>
    <div className="flex items-start gap-3 mb-3">
      <div className="min-w-0">
        <div className="font-display text-base font-semibold tracking-tight">{title}</div>
        {sub && <div className="text-[11.5px] text-ink-3 mt-0.5">{sub}</div>}
      </div>
      {right && <div className="ml-auto shrink-0">{right}</div>}
    </div>
    {children}
  </div>
);

const Tip = ({ active, payload, label, rows }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-3 py-2 text-[11.5px]"
      style={{ background: "rgba(255,255,255,0.97)", border: "1px solid rgba(15,18,38,0.1)", boxShadow: "0 12px 28px rgba(15,18,38,0.14)" }}>
      <div className="font-semibold mb-1">{label}</div>
      {(rows || payload).map((p) => (
        <div key={p.name || p.dataKey} className="flex items-center gap-2 whitespace-nowrap">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span className="text-ink-3">{p.name}</span>
          <b className="ml-auto">{usd2(p.value)}</b>
        </div>
      ))}
    </div>
  );
};

const Toggle = ({ value, onChange, options, testid }) => (
  <div className="inline-flex rounded-xl p-0.5" style={{ background: "rgba(15,18,38,0.05)" }} data-testid={testid}>
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className="px-2.5 py-1 rounded-[10px] text-[11.5px] font-semibold transition-colors"
        style={value === v ? { background: "white", color: "#0F1226", boxShadow: "0 2px 6px rgba(15,18,38,0.1)" }
                           : { color: "rgba(15,18,38,0.55)" }}
        data-testid={`${testid}-${v}`}>{label}</button>
    ))}
  </div>
);

/** Everything the partner can chart about their own book. */
export default function EarningsCharts({ d }) {
  const [mode, setMode] = useState("mine");
  // The API hands months newest-first for its table; a chart has to read
  // left-to-right in time or a rising book looks like a collapsing one.
  const months = useMemo(() => [...(d.months || [])]
    .sort((x, y) => String(x.month).localeCompare(String(y.month)))
    .map((m) => ({
      ...m,
      label: monLabel(m.month),
      state: m.paid ? "paid" : m.booked ? "due" : "accruing",
    })), [d.months]);

  const cumulative = useMemo(() => {
    let mine = 0; let theirs = 0;
    return months.map((m) => {
      mine += m.my_earnings || 0;
      theirs += m.client_obligation || 0;
      return { ...m, mine: +mine.toFixed(2), theirs: +theirs.toFixed(2) };
    });
  }, [months]);

  const byClient = useMemo(
    () => (d.by_client || []).map((c) => ({ ...c, short: shortName(c.name) })).slice(0, 10),
    [d.by_client]);

  const plans = useMemo(() => {
    const by = {};
    (d.by_client || []).forEach((c) => {
      const k = c.plan || "Unassigned";
      by[k] = by[k] || { name: k, value: 0, clients: 0 };
      by[k].value += c.capital || 0;
      by[k].clients += 1;
    });
    return Object.values(by).sort((a, b) => b.value - a.value);
  }, [d.by_client]);

  // One line per client, so you can see who is carrying the book and who has
  // just come on. Top six by earnings — more than that is spaghetti.
  const [clientMetric, setClientMetric] = useState("my_earnings");
  const clientLines = useMemo(() => {
    const rows = d.client_months || [];
    if (!rows.length) return { data: [], names: [] };
    const top = (d.by_client || []).slice(0, 6);
    const idToName = {};
    top.forEach((c) => { idToName[c.client_id] = shortName(c.name); });
    const byMonth = {};
    rows.forEach((r) => {
      const name = idToName[r.client_id];
      if (!name) return;
      const slot = (byMonth[r.month] = byMonth[r.month] || { month: r.month, label: monLabel(r.month) });
      slot[name] = (slot[name] || 0) + (r[clientMetric] || 0);
    });
    return {
      data: Object.values(byMonth).sort((x, y) => x.month.localeCompare(y.month)),
      names: top.map((c) => shortName(c.name)),
    };
  }, [d.client_months, d.by_client, clientMetric]);

  const best = months.reduce((a, m) => (m.my_earnings > (a?.my_earnings || 0) ? m : a), null);
  const avg = months.length
    ? months.reduce((a, m) => a + (m.my_earnings || 0), 0) / months.length : 0;
  const capitalNow = months.length ? months[months.length - 1].capital : 0;

  if (!months.length) return null;

  return (
    <div className="space-y-3" data-testid="partner-earnings-charts">
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-3">
        <Box title="Month by month" testid="chart-monthly"
          sub={`Your share each month · best was ${usd2(best?.my_earnings)} in ${best?.label} · ${usd2(avg)} average`}
          right={<Toggle value={mode} onChange={setMode} testid="chart-monthly-mode"
            options={[["mine", "My earnings"], ["both", "vs clients"]]} />}>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={months} margin={{ top: 4, right: 4, left: -4, bottom: 0 }} barGap={2}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={axis} axisLine={false} tickLine={false} tickFormatter={compact} width={58} />
              <Tooltip content={<Tip />} cursor={{ fill: "rgba(15,18,38,0.04)" }} />
              {mode === "both" && (
                <Bar dataKey="client_obligation" name="Owed to your clients" fill={CLIENTS} radius={[4, 4, 0, 0]} maxBarSize={22} />
              )}
              <Bar dataKey="my_earnings" name="Your earnings" radius={[4, 4, 0, 0]} maxBarSize={22}>
                {months.map((m) => (
                  <Cell key={m.month} fill={m.state === "paid" ? MINE : m.state === "due" ? "#0EA5E9" : "rgba(11,143,120,0.38)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2 text-[11px] text-ink-3 flex-wrap">
            {[["paid to you", MINE], ["due to you", "#0EA5E9"], ["still accruing", "rgba(11,143,120,0.38)"],
              ...(mode === "both" ? [["owed to your clients", CLIENTS]] : [])].map(([k, col]) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: col }} /> {k}
              </span>
            ))}
          </div>
        </Box>

        <Box title="Capital under management" sub={`${usd0(capitalNow)} working for your clients right now`} testid="chart-capital">
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={months} margin={{ top: 4, right: 4, left: -4, bottom: 0 }}>
              <defs>
                <linearGradient id="capFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CAPITAL} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={CAPITAL} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={axis} axisLine={false} tickLine={false} tickFormatter={compact} width={58} />
              <Tooltip content={<Tip />} />
              <Area type="monotone" dataKey="capital" name="Client capital" stroke={CAPITAL}
                strokeWidth={2.4} fill="url(#capFill)" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="text-[11px] text-ink-3 mt-2">
            Each step up is a client's funds being confirmed.
          </div>
        </Box>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-3">
        <Box title="Earnings, adding up" sub="Yours against what your clients are owed, running total" testid="chart-cumulative">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={cumulative} margin={{ top: 4, right: 6, left: -4, bottom: 0 }}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={axis} axisLine={false} tickLine={false} tickFormatter={compact} width={58} />
              <Tooltip content={<Tip />} />
              <Line type="monotone" dataKey="theirs" name="Owed to your clients" stroke={CLIENTS}
                strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="mine" name="Your earnings" stroke={MINE}
                strokeWidth={2.6} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Box title="Where the money sits" sub={`${plans.length} plan${plans.length === 1 ? "" : "s"} · capital by plan`} testid="chart-plans">
          <div className="flex items-center gap-3">
            <ResponsiveContainer width="55%" height={220}>
              <PieChart>
                <Pie data={plans} dataKey="value" nameKey="name" innerRadius={48} outerRadius={82}
                  paddingAngle={2} stroke="none">
                  {plans.map((p, i) => <Cell key={p.name} fill={PLAN_COLOURS[i % PLAN_COLOURS.length]} />)}
                </Pie>
                <Tooltip content={<Tip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 min-w-0 space-y-2">
              {plans.map((p, i) => (
                <div key={p.name} className="text-[11.5px]" data-testid={`plan-legend-${i}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: PLAN_COLOURS[i % PLAN_COLOURS.length] }} />
                    <span className="truncate font-medium">{p.name}</span>
                  </div>
                  <div className="text-ink-3 pl-4">{usd0(p.value)} · {p.clients} client{p.clients === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          </div>
        </Box>
      </div>

      {clientLines.data.length > 1 && (
        <Box title="Client by client, month by month" testid="chart-client-lines"
          sub={`Your top ${clientLines.names.length} clients, tracked across the year`}
          right={<Toggle value={clientMetric} onChange={setClientMetric} testid="chart-client-lines-metric"
            options={[["my_earnings", "My earnings"], ["client_obligation", "Owed to them"], ["capital", "Their capital"]]} />}>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={clientLines.data} margin={{ top: 4, right: 8, left: -4, bottom: 0 }}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={axis} axisLine={false} tickLine={false} tickFormatter={compact} width={58} />
              <Tooltip content={<Tip />} />
              <Legend verticalAlign="top" align="right" iconType="plainline" iconSize={14}
                wrapperStyle={{ fontSize: 11, color: "rgba(15,18,38,0.55)", paddingBottom: 8 }} />
              {clientLines.names.map((n, i) => (
                <Line key={n} type="monotone" dataKey={n} name={n} stroke={PLAN_COLOURS[i % PLAN_COLOURS.length]}
                  strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <div className="text-[11px] text-ink-3 mt-2">
            A line starting late is a client whose funds were confirmed later in the year.
          </div>
        </Box>
      )}

      <Box title="Your clients, ranked" sub="What each one earns you, and the capital behind it" testid="chart-by-client">
        <ResponsiveContainer width="100%" height={Math.max(180, byClient.length * 38)}>
          <BarChart data={byClient} layout="vertical" margin={{ top: 0, right: 12, left: 6, bottom: 0 }} barGap={1}>
            <CartesianGrid stroke={grid} horizontal={false} />
            <XAxis type="number" tick={axis} axisLine={false} tickLine={false} tickFormatter={compact} />
            <YAxis type="category" dataKey="short" tick={{ ...axis, fontSize: 11 }} axisLine={false} tickLine={false} width={96} />
            <Tooltip content={<Tip />} cursor={{ fill: "rgba(15,18,38,0.04)" }} />
            <Legend verticalAlign="top" align="right" iconType="square" iconSize={9}
              wrapperStyle={{ fontSize: 11, color: "rgba(15,18,38,0.55)", paddingBottom: 6 }} />
            <Bar dataKey="my_earnings" name="Your earnings" fill={MINE} radius={[0, 4, 4, 0]} maxBarSize={13} />
            <Bar dataKey="client_obligation" name="Owed to them" fill={CLIENTS} radius={[0, 4, 4, 0]} maxBarSize={13} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </div>
  );
}
